import { computed, inject, Injectable, signal } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { IEmployee } from 'app/interfaces/employee';
import { INotificationPreferences } from 'app/interfaces/notification-preferences';
import { IPage } from 'app/interfaces/page';
import { filter, firstValueFrom, map, Observable } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventType, InteractionStatus } from '@azure/msal-browser';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  apollo = inject(Apollo);
  msalService = inject(MsalService);
  msalBroadcastService = inject(MsalBroadcastService);

  private readonly activeAccount = signal<AccountInfo | null>(null);

  private _user = signal<IEmployee | null>(null);

  user = computed(() => this._user());
  isAdmin = computed(() => this.getActiveAccountRoles().includes(environment.azureAd.GroupId.Admin));
  isCoach = computed(() => this.getActiveAccountRoles().includes(environment.azureAd.GroupId.Coach));

  findAllEmployeesPaged(page: number, size: number, search?: string) {
    return this.apollo.query<{ findAllEmployeesPaged: IPage<IEmployee> }>({
      query: gql`
        query findAllEmployeesPaged($page: Int!, $size: Int!, $search: String) {
          findAllEmployeesPaged(page: $page, size: $size, search: $search) {
            content {
              id
              firstName
              lastName
              email
              phoneNumber
            }
            pageInfo {
              totalElements
              totalPages
              size
              number
              first
              last
            }
          }
        }
      `,
      variables: { page, size, search },
      fetchPolicy: 'network-only',
    });
  }

  me() {
    return this.apollo.query<{ me: IEmployee | null }>({
      query: gql`
        query me {
          me {
            id
            oid
            firstName
            lastName
            email
            hasCustomNumber
            phoneNumber
            participationScore
            notificationPreferences {
              mail
              sms
            }
          }
        }
      `,
      fetchPolicy: 'network-only',
    });
  }

  getCurrentUserId(): Observable<number | null> {
    return this.apollo
      .query<{ me: { id?: number } | null }>({
        query: gql`
          query me {
            me {
              id
            }
          }
        `,
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.me?.id ?? null));
  }

  updateNotificationPreferences(employeeId: number, input: INotificationPreferences) {
    return this.apollo.mutate<{
      updateNotificationPreferences: number;
    }>({
      mutation: gql`
        mutation updateNotificationPreferences(
          $employeeId: ID!
          $input: NotificationPreferencesInput!
        ) {
          updateNotificationPreferences(employeeId: $employeeId, input: $input)
        }
      `,
      variables: {
        employeeId: Number(employeeId),
        input,
      },
      fetchPolicy: 'network-only',
    });
  }

  updatePhoneNumber(employeeId: number, hasCustomNumber: boolean, phoneNumber?: string) {
    return this.apollo.mutate<{ updatePhoneNumber: string }>({
      mutation: gql`
        mutation updatePhoneNumber(
          $employeeId: ID!
          $hasCustomNumber: Boolean!
          $phoneNumber: String
        ) {
          updatePhoneNumber(
            employeeId: $employeeId
            hasCustomNumber: $hasCustomNumber
            phoneNumber: $phoneNumber
          )
        }
      `,
      variables: {
        employeeId: Number(employeeId),
        hasCustomNumber,
        phoneNumber,
      },
      fetchPolicy: 'network-only',
    });
  }

  gdprDeleteEmployee(employeeId: number, deleteSurveyResponses: boolean) {
    return this.apollo.mutate<{ gdprDeleteEmployee: boolean }>({
      mutation: gql`
        mutation gdprDeleteEmployee($employeeId: ID!, $deleteSurveyResponses: Boolean!) {
          gdprDeleteEmployee(employeeId: $employeeId, deleteSurveyResponses: $deleteSurveyResponses)
        }
      `,
      variables: {
        employeeId: Number(employeeId),
        deleteSurveyResponses,
      },
      fetchPolicy: 'network-only',
    });
  }

  hardDeleteEmployee(employeeId: number) {
    return this.apollo.mutate<{ hardDeleteEmployee: boolean }>({
      mutation: gql`
        mutation hardDeleteEmployee($employeeId: ID!) {
          hardDeleteEmployee(employeeId: $employeeId)
        }
      `,
      variables: {
        employeeId: Number(employeeId),
      },
      fetchPolicy: 'network-only',
    });
  }

  async loadUser(): Promise<void> {
    try {
      const result = await firstValueFrom(this.me());
      this._user.set(result.data?.me ?? null);
    } catch {
      this._user.set(null);
    }
  }

  constructor() {
    this.initializeAuth();
  }

  private checkAndSetActiveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    this.updateActiveAccount();
  }

  private updateActiveAccount() {
    this.activeAccount.set(this.msalService.instance.getActiveAccount());
  }

  private getActiveAccountRoles(): string[]  {
    return this.activeAccount()?.idTokenClaims!['groups'] as string[] ?? [];
  }

  private initializeAuth() {
    this.msalService.handleRedirectObservable().subscribe({
      next: () => this.checkAndSetActiveAccount(),
      error: (error) => console.error('Error handling redirect:', error),
    });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg) =>
            msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED ||
            msg.eventType === EventType.LOGIN_SUCCESS,
        ),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.checkAndSetActiveAccount());

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.checkAndSetActiveAccount());
  }
}
